import { createRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Route as TenantLayoutRoute } from "./_layout";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { SpzMobileBreakdownTable } from "../../components/custom/SpzMobileBreakdownTable";
// Recharts lazy — viz tenant/dashboard.tsx pro detail.
const SpzBarChart = lazy(() =>
  import("../../components/custom/SpzBarChart").then((m) => ({ default: m.SpzBarChart })),
);
import { ClosedMonthsTable } from "../../components/custom/ClosedMonthsTable";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useTenantDashboardOverview } from "../../hooks/useTenantDashboard";
import {
  eurIsPositive,
  type ClosedMonthRow,
} from "../../schemas/tenantDashboard";

/**
 * UC-005 tenant month detail (Phase 23) — drill-down z David dashboard
 * `<ClosedMonthsTable>` na konkrétní budget. Reuses existing 12M overview
 * dataset a filtruje na zvolený měsíc (server může později vrátit dedicated
 * endpoint; pro Phase 23 stačí client-side filter).
 *
 * Per princip #6 — backend RLS zabrání cross-tenant URL hacku (David na cizí
 * tenant URL → 403 + TENANT_ACCESS_DENIED per REQ-EH-007).
 */
export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/month/$monthId",
  component: MonthDetail,
});

function MonthDetail() {
  const { monthId } = Route.useParams();
  const { t } = useTranslation("tenant");
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";
  const query = useTenantDashboardOverview("12M");
  const data = query.data;

  const monthRow: ClosedMonthRow | undefined = data?.closed_months.find(
    (r) => r.budget_id === monthId,
  );

  const monthSpzBreakdown =
    data && monthRow
      ? data.spz_breakdown
      : [];

  const sumEurPositive = monthRow ? eurIsPositive(monthRow.total_eur) : false;

  return (
    <div className="flex flex-col gap-4" data-testid="month-detail">
      <header className="flex flex-col gap-1">
        <h1>{`Měsíc — ${monthRow ? monthRow.month : monthId}`}</h1>
        {monthRow && (
          <p className="m-0 text-tertiary">
            {monthRow.billing_group_code} · {monthRow.spz_count} SPZ ·{" "}
            {monthRow.transaction_count} transakcí
          </p>
        )}
      </header>

      {query.isLoading && (
        <div data-testid="month-detail-loading">
          <LoadingIndicator />
        </div>
      )}

      {query.isError && (
        <InlineAlert variant="warning" title={t("dashboard.david.loadingError")}>
          <p>
            {query.error instanceof Error
              ? query.error.message
              : String(query.error ?? "")}
          </p>
        </InlineAlert>
      )}

      {!query.isLoading && !query.isError && !monthRow && (
        <InlineAlert variant="info" title={`Měsíc ${monthId}`}>
          <p>Detail tohoto měsíce není v aktuálním přehledu k dispozici.</p>
        </InlineAlert>
      )}

      {monthRow && (
        <>
          <Card>
            <CardHeader title={t("dashboard.david.spzBar.title")} />
            <CardBody>
              <Suspense fallback={<div className="h-80 animate-pulse bg-secondary rounded" aria-hidden="true" />}>
                <SpzBarChart points={monthSpzBreakdown} />
              </Suspense>
              {isMobile && (
                <SpzMobileBreakdownTable
                  points={monthSpzBreakdown}
                  showEur={sumEurPositive}
                />
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={t("dashboard.david.closedMonths.title")} />
            <CardBody>
              <ClosedMonthsTable rows={[monthRow]} />
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
